import { Color } from '@coast/schemas';
import { isNil } from 'lodash';
import { AnchoredSheet, AnchoredSheetTitleBar } from '@shared/components/AnchoredSheet';
import { View } from '@shared/components/View';
import { getTagOption } from '@shared/components/blocks/lib/tags';
import { useTagOptions } from '@shared/components/blocks/lib/tags/hooks';
import { Tag } from '@shared/components/blocks/lib/tags/tag';
import { MBHover } from '@shared/components/hover';
import { MBIconV2, MBIconV2Name } from '@shared/components/image';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBMultiSelect } from '@shared/components/multiSelect';
import { Colors, grid } from '@shared/styles';
import { useIsDesktop } from '@shared/util/hooks';
import styles from './TagComponentForm.module.css';

export function TagComponentForm({
  fieldName,
  field,
  onUpdateField,
  anchorName,
  component,
  onClose,
  loading,
}) {
  const { options } = useTagOptions(fieldName);
  const tagOptions = field?.fieldType ? field.tagOptions : options;
  const activeOptions = tagOptions?.filter(o => isNil(o.archivedAt)) || [];
  const selectedOptions = tagOptions
    .filter(option => field?.includes(option.slug))
    .map(o => o.slug);
  const max = component.multiselect ? 0 : 1;
  const isDesktop = useIsDesktop();

  const onChange = newValues => {
    onUpdateField(fieldName, newValues);
    if (max === 1) {
      onClose();
    }
  };

  const match = (value: string, query: string) => {
    const label = tagOptions.find(o => o.slug === value)?.label;
    return label.toLowerCase().includes(query.toLowerCase().trim());
  };

  return (
    <AnchoredSheet
      anchorName={anchorName}
      onClose={onClose}
      renderTitleBar={
        isDesktop
          ? () => null
          : () => (
              <AnchoredSheetTitleBar
                title={component.label}
                rightIcon={'exit' as MBIconV2Name}
                rightIconColor={Colors.v2.purple}
                onRightIconPress={onClose}
              />
            )
      }
      className={styles.tagComponentFormSheet}
    >
      <View
        flex
        style={{
          width: isDesktop ? grid(44) : '100%',
          minHeight: isDesktop ? grid(44) : 0,
          maxHeight: isDesktop ? grid(44) : '100%',
          justifyContent: loading ? 'center' : 'flex-start',
        }}
      >
        {loading && <MBLoadingIndicator />}
        {!loading && (
          <MBMultiSelect
            values={activeOptions.map(option => option.slug)}
            selectedValues={selectedOptions}
            renderItem={item => {
              const option = getTagOption(item, tagOptions);
              if (!option) {
                return null;
              }
              return (
                <MBHover hoverStyle={{ backgroundColor: Colors.v2.backgroundGrayLight }}>
                  <View row centerVertically>
                    <View marginRight={1}>
                      <MBIconV2 name="tag" color={Colors.v2.purple} size={grid(3)} />
                    </View>
                    <Tag color={option.colorName as Color} label={option.label} />
                  </View>
                </MBHover>
              );
            }}
            editMode={false}
            onChange={onChange}
            match={match}
            max={max}
            isRequired={component.required}
          />
        )}
      </View>
    </AnchoredSheet>
  );
}
